import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useEffect, useMemo, useState } from 'react';
import { useCurrentUser } from '@agro1desenvolvimento/react-hooks';
import {
  Card, Dropdown, Calendar, Checkbox, InputText, Button, InputTextarea,
} from '@agro1desenvolvimento/react-components';
import { isNil } from 'lodash';
import { AnaliseStatus } from '../../../../../../@types/sementes/analiseStatus';

interface Analista {
  id: string;
  nome: string;
}

const MIN_SOLUCAO = 0;
const MAX_SOLUCAO = 100;

interface FormValues {
  analistaId: string;
  data: Date | undefined;
  gerarBASRIR: boolean;
  solucao: string;
  repeticoes: 2 | 4;
  sementesPorRepeticao: 50 | 100;
  observacao: string;
}

interface Props {
  analistas: Analista[];
  searchAnalistas: (query: string) => Promise<Analista[]>;
  onSubmit: (values: FormValues) => void;
  loading?: boolean;
  initialValues?: Partial<FormValues>;
  submitButtonText?: string;
  bloqueiaRepeticoes?: boolean;
  status?: AnaliseStatus;
}

const validationSchema = Yup.object({
  analistaId: Yup.string().required('Analista é obrigatório'),
  data: Yup.date()
    .required('Data é obrigatória')
    .test('max-date', 'Data não pode ser maior que hoje', (value) => {
      if (!value) return true;
      const hoje = new Date();
      hoje.setHours(23, 59, 59, 999);
      return value <= hoje;
    }),
  solucao: Yup.string()
    .required('Solução é obrigatória')
    .matches(/^\d+([,.]?\d+)?$/, 'Solução deve ser um número válido')
    .test('solucao-range', `Solução deve estar entre ${MIN_SOLUCAO} e ${MAX_SOLUCAO}`, (value) => {
      if (!value) return true;
      const numValue = parseFloat(value.replace(',', '.'));
      return numValue >= MIN_SOLUCAO && numValue <= MAX_SOLUCAO;
    }),
  repeticoes: Yup.number()
    .required('Repetições é obrigatório')
    .oneOf([2, 4], 'Deve ser 2 ou 4'),
  sementesPorRepeticao: Yup.number()
    .required('Sementes por repetição é obrigatório')
    .oneOf([50, 100], 'Deve ser 50 ou 100'),
});

export default function FormularioTetrazolioAnaliseInicial(
  {
    analistas,
    searchAnalistas,
    onSubmit,
    loading = false,
    initialValues: propInitialValues,
    submitButtonText,
    bloqueiaRepeticoes = false,
    status,
  }: Props,
) {
  const currentUser = useCurrentUser();
  const [dataInicial] = useState(new Date());
  const [analistasOptions, setAnalistasOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);

  const isDisabled = status === 'Realizado';

  const analistaIdPadrao = useMemo(() => {
    if (!currentUser || analistas.length === 0) return '';

    const usuarioEncontrado = analistas.find((a) => a.id === currentUser.id);
    return usuarioEncontrado ? usuarioEncontrado.id : '';
  }, [analistas, currentUser]);

  useEffect(() => {
    setAnalistasOptions(
      analistas.map((analista) => ({ label: analista.nome, value: analista.id })),
    );
  }, [analistas]);

  const formik = useFormik<FormValues>({
    initialValues: {
      analistaId: propInitialValues?.analistaId || analistaIdPadrao,
      data: propInitialValues?.data || dataInicial,
      gerarBASRIR: isNil(propInitialValues?.gerarBASRIR) ? true : propInitialValues!.gerarBASRIR,
      solucao: propInitialValues?.solucao || '',
      repeticoes: propInitialValues?.repeticoes || 2,
      sementesPorRepeticao: propInitialValues?.sementesPorRepeticao || 50,
      observacao: propInitialValues?.observacao || '',
    },
    validationSchema,
    onSubmit: (values) => {
      onSubmit(values);
    },
    enableReinitialize: true,
  });

  useEffect(() => {
    if (analistaIdPadrao && !formik.values.analistaId) {
      formik.setFieldValue('analistaId', analistaIdPadrao);
    }
  }, [analistaIdPadrao]);

  const handleFilterAnalistas = async (event: { filter: string }) => {
    const query = event.filter;

    if (!query || query.trim().length === 0) {
      setAnalistasOptions(analistas.map((a) => ({ label: a.nome, value: a.id })));
      return;
    }

    try {
      const resultados = await searchAnalistas(query);
      setAnalistasOptions(resultados.map((a) => ({ label: a.nome, value: a.id })));
    } catch (err) {
      console.error('Erro ao buscar analistas:', err);
    }
  };

  const isFormValid = formik.isValid
    && !!formik.values.analistaId
    && !!formik.values.data
    && !!formik.values.solucao;

  if (!formik.isValid && Object.keys(formik.errors).length > 0) {
    console.warn('[FormularioTetrazolioAnaliseInicial] Formulário inválido:', formik.errors);
  }

  return (
    <Card className="formulario-tetrazolio-analise-inicial">
      <h3 className="title">Tetrazólio</h3>
      <p className="subtitle">Complete os campos abaixo para iniciar a análise das sementes</p>

      <form onSubmit={(e) => { e.preventDefault(); /* Bloqueia submit com Enter - apenas botão pode submeter */ }} className="form">
        <div className="form-field">
          <label htmlFor="analistaId">Analista</label>
          <Dropdown
            id="analistaId"
            name="analistaId"
            value={formik.values.analistaId}
            options={analistasOptions}
            onChange={(e) => formik.setFieldValue('analistaId', e.value)}
            placeholder="Selecione..."
            filter
            filterPlaceholder="Buscar analista..."
            onFilter={handleFilterAnalistas}
            className={formik.errors.analistaId && formik.touched.analistaId ? 'p-invalid' : ''}
            disabled={isDisabled}
          />
          {formik.errors.analistaId && formik.touched.analistaId && (
            <span className="error-message">{formik.errors.analistaId}</span>
          )}
        </div>

        <div className="form-field">
          <label htmlFor="data">Data</label>
          <Calendar
            id="data"
            name="data"
            value={formik.values.data}
            onChange={(e) => formik.setFieldValue('data', e.value)}
            dateFormat="dd/mm/yy"
            className={formik.errors.data && formik.touched.data ? 'p-invalid' : ''}
            disabled={isDisabled}
          />
          {formik.errors.data && formik.touched.data && (
            <span className="error-message">{formik.errors.data}</span>
          )}
        </div>

        <div className="form-field checkbox-field">
          <div className="checkbox-wrapper">
            <Checkbox
              inputId="gerarBASRIR"
              name="gerarBASRIR"
              checked={formik.values.gerarBASRIR}
              onChange={(e) => formik.setFieldValue('gerarBASRIR', e.checked)}
              disabled={isDisabled}
            />
            <label htmlFor="gerarBASRIR" className="checkbox-label">Gerar BAS/RIR</label>
          </div>
        </div>

        <div className="form-field">
          <label htmlFor="solucao">Solução (%)</label>
          <InputText
            id="solucao"
            name="solucao"
            value={formik.values.solucao}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="0,0000"
            className={formik.errors.solucao && formik.touched.solucao ? 'p-invalid' : ''}
            disabled={isDisabled}
          />
          {formik.errors.solucao && formik.touched.solucao && (
            <span className="error-message">{formik.errors.solucao}</span>
          )}
        </div>

        <div className="form-field">
          <label>Repetições</label>
          <div className="button-group">
            <Button
              type="button"
              // se tiver uma rejeitada, a api devolve 3 repetições, mas na UI continua sendo 2
              className={`seed-button
                ${formik.values.repeticoes === 2 || (formik.values.repeticoes as number) === 3 ? 'active' : ''}
                ${bloqueiaRepeticoes || isDisabled ? 'disabled' : ''}`}
              onClick={() => formik.setFieldValue('repeticoes', 2)}
              disabled={bloqueiaRepeticoes || isDisabled}
              style={bloqueiaRepeticoes || isDisabled ? {
                opacity: 0.5,
                cursor: 'not-allowed',
                borderWidth: '2px',
                pointerEvents: 'none',
              } : {}}
            >
              2
            </Button>
            <Button
              type="button"
              // se tiver uma rejeitada, a api devolve 5 repetições, mas na UI continua sendo 2
              className={`seed-button
                ${formik.values.repeticoes === 4 || (formik.values.repeticoes as number) === 5 ? 'active' : ''}
                ${bloqueiaRepeticoes || isDisabled ? 'disabled' : ''}`}
              onClick={() => formik.setFieldValue('repeticoes', 4)}
              disabled={bloqueiaRepeticoes || isDisabled}
              style={bloqueiaRepeticoes || isDisabled ? {
                opacity: 0.5,
                cursor: 'not-allowed',
                borderWidth: '2px',
                pointerEvents: 'none',
              } : {}}
            >
              4
            </Button>
          </div>
        </div>

        <div className="form-field">
          <label>Sementes por Repetição</label>
          <div className="button-group">
            <Button
              type="button"
              className={`seed-button ${formik.values.sementesPorRepeticao === 50 ? 'active' : ''} ${bloqueiaRepeticoes || isDisabled ? 'disabled' : ''}`}
              onClick={() => formik.setFieldValue('sementesPorRepeticao', 50)}
              disabled={bloqueiaRepeticoes || isDisabled}
              style={bloqueiaRepeticoes || isDisabled ? {
                opacity: 0.5,
                cursor: 'not-allowed',
                borderWidth: '2px',
                pointerEvents: 'none',
              } : {}}
            >
              50
            </Button>
            <Button
              type="button"
              className={`seed-button ${formik.values.sementesPorRepeticao === 100 ? 'active' : ''} ${bloqueiaRepeticoes || isDisabled ? 'disabled' : ''}`}
              onClick={() => formik.setFieldValue('sementesPorRepeticao', 100)}
              disabled={bloqueiaRepeticoes || isDisabled}
              style={bloqueiaRepeticoes || isDisabled ? {
                opacity: 0.5,
                cursor: 'not-allowed',
                borderWidth: '2px',
                pointerEvents: 'none',
              } : {}}
            >
              100
            </Button>
          </div>
        </div>

        <div className="form-field observacao-field">
          <label htmlFor="observacao">Observação (opcional)</label>
          <InputTextarea
            id="observacao"
            name="observacao"
            value={formik.values.observacao}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Digite suas observações aqui..."
            rows={1}
            autoResize={false}
            style={{ resize: 'none', overflow: 'auto' }}
            disabled={isDisabled}
          />
        </div>

        <div className="form-field submit-field">
          <Button
            type="button"
            className={`submit-button ${isFormValid ? 'valid' : 'invalid'}`}
            disabled={!isFormValid || loading}
            onClick={() => formik.handleSubmit()}
          >
            {loading ? 'Carregando...' : (submitButtonText || 'Iniciar Análise')}
          </Button>
        </div>
      </form>
    </Card>
  );
}
